import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession, waitForTier } from '../util/amplifyAuth';
import { getSubscriptionStatus } from '../components/API';
import UserHeader from '../components/UserHeader/UserHeader';
import '../styles/Pricing.css';

function PaymentSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const [authChecked, setAuthChecked] = useState(false);
    // The Cognito group is added asynchronously by the webhook. Poll a token refresh in
    // the background until the new tier propagates, and gate "Go to Dashboard" on it so
    // the user doesn't land on a still-locked dashboard — no sign-out needed. billing.md §3.
    const [activating, setActivating] = useState(true);
    // When the new subscription is a free trial, show trial-specific copy (not charged yet,
    // trial-end date). Null until the status is fetched; falls back to the active copy.
    const [trialEndsAt, setTrialEndsAt] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                await fetchAuthSession();
                if (mounted) setAuthChecked(true);
            } catch {
                navigate('/', { replace: true });
                return;
            }
            try {
                await waitForTier(1);
            } catch { /* proceed regardless — resolves on next natural refresh */ }
            // Detect a trialing subscription so the copy reflects "trial started, not charged".
            try {
                const { subscription } = await getSubscriptionStatus();
                if (mounted && subscription?.status === 'trialing' && subscription.currentPeriodEnd) {
                    setTrialEndsAt(subscription.currentPeriodEnd);
                }
            } catch { /* non-fatal — fall back to generic active copy */ }
            if (mounted) setActivating(false);
        })();
        return () => { mounted = false; };
    }, [navigate]);

    if (!authChecked) return null;

    const isTrial = trialEndsAt != null;
    const trialEndDate = isTrial
        ? new Date(trialEndsAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        : null;

    return (
        <div className="pricing-container">
            <UserHeader />
            <div className="pricing-content">
                <div className="success-card">
                    <div className="success-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h1 className="success-title">{isTrial ? 'Your free trial has started' : 'Payment Successful'}</h1>
                    <p className="success-message">
                        {isTrial ? (
                            <>
                                Premium is unlocked for your free trial. <strong>You won&apos;t be charged
                                until {trialEndDate}</strong>, when your trial converts to a monthly
                                subscription. Cancel anytime before then from Billing and you won&apos;t be charged.
                            </>
                        ) : (
                            'Your Premium subscription is now active. It may take a moment for all features to unlock.'
                        )}
                    </p>
                    {sessionId && (
                        <p className="success-session">Session: {sessionId.slice(0, 20)}...</p>
                    )}
                    <div className="success-actions">
                        <button className="pricing-cta" onClick={() => navigate('/home')} disabled={activating}>
                            {activating ? 'Unlocking access…' : 'Go to Dashboard'}
                        </button>
                        <button className="success-secondary" onClick={() => navigate('/billing')}>
                            View Billing
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PaymentSuccess;
